import React, { useId } from "react";
import { OverlayTrigger, OverlayTriggerProps } from "react-bootstrap";
import Card from "ui/Card";
import styled from "styled-components/macro";
import Typography from "ui/Typography";

export interface TooltipProps
  extends Pick<
    OverlayTriggerProps,
    "children" | "placement" | "trigger" | "show" | "onToggle"
  > {
  label: React.ReactNode;
  autoDismiss?: number;
  onClose?: () => void;
  /**
   * Length of delay to show/hide overlay in milliseconds.
   */
  delay?:
    | number
    | {
        show: number;
        hide: number;
      };
  className?: string;
}

// TODO: Component Library: Use the reskinned Card once available
// TODO: Component Library: Once tooltip is finalized use proper space and size

// TODO: This is bootstraps default z-index for tooltips.
// Swap this with values from our theme when the z-index PR has been merged.
// https://github.com/chanzuckerberg/connections/pull/1196
// https://getbootstrap.com/docs/4.0/layout/overview/#z-index
const $Tooltip = styled(Card)
  .attrs({
    $noHoverState: false,
  })
  .withConfig({
    shouldForwardProp: (prop) =>
      !["arrowProps", "hasDoneInitialMeasure"].includes(prop),
  })`
  padding: ${({ theme }) => theme.SizeXs};
  max-width: 19.25rem;
  border: 1px solid ${({ theme }) => theme.ColorSpace200};
  background-color: ${({ theme }) => theme.ColorWhite};
  z-index: 1070;
  transition: none;
  word-break: break-word;
`;

export default function Tooltip({
  children,
  placement = "auto",
  trigger = ["hover", "focus"],
  show,
  onToggle,
  label,
  autoDismiss,
  onClose,
  delay = 0,
  className,
}: TooltipProps) {
  const id = useId();

  return (
    <OverlayTrigger
      placement={placement}
      flip={true}
      overlay={
        <$Tooltip id={id} role="tooltip" className={className}>
          <Typography size="bodySansSerif">{label}</Typography>
        </$Tooltip>
      }
      trigger={trigger}
      show={show}
      onToggle={onToggle}
      onEntered={() => {
        if (onClose && autoDismiss) {
          setTimeout(onClose, autoDismiss);
        }
      }}
      onExit={onClose}
      delay={delay}
    >
      {children}
    </OverlayTrigger>
  );
}
